import type { VisualDataResult } from "@/types/services/data";
import { logger } from "@/utils/debug";
import { indexedDBService } from "../storage/indexedDB";

class VisualDataProcessor {
  /**
   * Processes data for visual display and preserves geocoded coordinates
   *
   * @param sourceData - The original source data
   * @param sourceColumns - Optional source columns (will be extracted from data if not provided)
   * @param existingProcessedData - Optional existing processed data that may contain geocoded columns to preserve
   * @param dataflowId - Optional dataflow ID to load geocoded data from IndexedDB
   * @returns Promise that resolves with the data ready for display
   */
  async processDataForVisualization(
    sourceData: any[],
    sourceColumns?: string[],
    existingProcessedData?: { data: any[]; metadata: { columns: string[] } },
    dataflowId?: string
  ): Promise<VisualDataResult> {
    const geocodedDataMap = new Map<
      string,
      { lat: number | null; lon: number | null }
    >();
    let preservedLatColumn: string | null = null;
    let preservedLonColumn: string | null = null;

    let geocodedSourceData: {
      data: any[];
      metadata: { columns: string[] };
    } | null = null;
    if (dataflowId) {
      try {
        const indexedData = await indexedDBService.getTransformedData(
          dataflowId
        );
        if (indexedData && indexedData.data && indexedData.data.length > 0) {
          geocodedSourceData = indexedData;
        }
      } catch (error) {
        logger.warn("Failed to load geocoded data from IndexedDB:", error);
      }
    }

    if (!geocodedSourceData && existingProcessedData) {
      geocodedSourceData = existingProcessedData;
    }

    if (geocodedSourceData?.data && geocodedSourceData.data.length > 0) {
      const existingColumns = geocodedSourceData.metadata.columns || [];
      const latColumn = existingColumns.find((col) => /^latitude$/i.test(col));
      const lonColumn = existingColumns.find((col) => /^longitude$/i.test(col));

      if (latColumn && lonColumn) {
        preservedLatColumn = latColumn;
        preservedLonColumn = lonColumn;

        const sourceCols = sourceColumns || this.extractColumns(sourceData);

        const chunkSize = 1000;
        for (let i = 0; i < geocodedSourceData.data.length; i += chunkSize) {
          const chunk = geocodedSourceData.data.slice(i, i + chunkSize);
          chunk.forEach((geocodedRow, localIndex) => {
            const geocodedIndex = i + localIndex;
            const commonColumns = sourceCols.filter((col) =>
              geocodedRow.hasOwnProperty(col)
            );

            if (commonColumns.length > 0) {
              const signature = commonColumns
                .map((col) => String(geocodedRow[col] ?? ""))
                .join("|");

              if (
                geocodedRow[latColumn] != null ||
                geocodedRow[lonColumn] != null
              ) {
                geocodedDataMap.set(signature, {
                  lat: geocodedRow[latColumn],
                  lon: geocodedRow[lonColumn],
                });
              }
            } else {
              if (geocodedIndex < sourceData.length) {
                const sourceRow = sourceData[geocodedIndex];
                const sourceSignature = sourceCols
                  .map((col) => String(sourceRow[col] ?? ""))
                  .join("|");
                if (
                  geocodedRow[latColumn] != null ||
                  geocodedRow[lonColumn] != null
                ) {
                  geocodedDataMap.set(sourceSignature, {
                    lat: geocodedRow[latColumn],
                    lon: geocodedRow[lonColumn],
                  });
                }
              }
            }
          });

          if (i + chunkSize < geocodedSourceData.data.length) {
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
        }
      }
    }

    if (existingProcessedData) {
      const existingColumns = existingProcessedData.metadata.columns || [];
      const hasLat = existingColumns.some((col) => /^latitude$/i.test(col));
      const hasLon = existingColumns.some((col) => /^longitude$/i.test(col));

      if (hasLat && hasLon) {
        return {
          data: existingProcessedData.data,
          metadata: {
            rowCount: existingProcessedData.data.length,
            columnCount: existingColumns.length,
            columns: existingColumns,
          },
        };
      }
    }

    const columns = sourceColumns || this.extractColumns(sourceData);
    let data = sourceData;

    if (preservedLatColumn && preservedLonColumn && geocodedDataMap.size > 0) {
      const finalColumns = [...columns];
      if (!finalColumns.includes(preservedLatColumn)) {
        finalColumns.push(preservedLatColumn);
      }
      if (!finalColumns.includes(preservedLonColumn)) {
        finalColumns.push(preservedLonColumn);
      }

      const sourceCols = sourceColumns || this.extractColumns(sourceData);

      const chunkSize = 1000;
      const chunks: any[][] = [];
      for (let i = 0; i < data.length; i += chunkSize) {
        chunks.push(data.slice(i, i + chunkSize));
      }

      const processedChunks: any[][] = [];
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        const processedChunk = chunk.map((row, localIndex) => {
          const rowIndex = chunkIndex * chunkSize + localIndex;
          const commonColumns = sourceCols.filter((col) =>
            row.hasOwnProperty(col)
          );

          let geocoded: { lat: number | null; lon: number | null } | undefined;

          if (commonColumns.length > 0) {
            const signature = commonColumns
              .map((col) => String(row[col] ?? ""))
              .join("|");
            geocoded = geocodedDataMap.get(signature);
          }

          if (!geocoded && rowIndex < sourceData.length) {
            const sourceRow = sourceData[rowIndex];
            const sourceSignature = sourceCols
              .map((col) => String(sourceRow[col] ?? ""))
              .join("|");
            geocoded = geocodedDataMap.get(sourceSignature);
          }

          if (geocoded) {
            return {
              ...row,
              [preservedLatColumn!]: geocoded.lat,
              [preservedLonColumn!]: geocoded.lon,
            };
          }
          return row;
        });
        processedChunks.push(processedChunk);

        if (chunkIndex < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      data = processedChunks.flat();

      return {
        data,
        metadata: {
          rowCount: data.length,
          columnCount: finalColumns.length,
          columns: finalColumns,
        },
      };
    }

    return {
      data: sourceData,
      metadata: {
        rowCount: sourceData.length,
        columnCount: columns.length,
        columns,
      },
    };
  }

  /**
   * Extracts column names from data
   * @param data - The data to extract columns from
   * @returns Array of column names
   */
  private extractColumns(data: any[]): string[] {
    if (data.length === 0) return [];
    return Object.keys(data[0]);
  }
}

export const visualDataProcessor = new VisualDataProcessor();
